import React, { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { toast } from "react-toastify";
import { QUESTION_TYPES, questionTypeLabel } from "../../../data/loca/satisfaction";

// Gestion d'une liste de questions (outil ou questions generales) :
// ajout, modification, suppression / desactivation, reordonnancement, (re)generation.
// Les appels API sont fournis par le parent pour reutiliser le composant cote loueur et cote admin.

const emptyForm = () => ({ label: "", type: "Rating", options: "", isRequired: false, isActive: true });

function toForm(question) {
    return {
        label: question.label ?? "",
        type: question.type ?? "Rating",
        options: (question.options ?? []).join(", "),
        isRequired: Boolean(question.isRequired),
        isActive: question.isActive !== false
    };
}

function toInput(form) {
    return {
        label: form.label.trim(),
        type: form.type,
        options: form.type === "Choice"
            ? form.options.split(/[,;\n]/).map((option) => option.trim()).filter(Boolean)
            : null,
        isRequired: Boolean(form.isRequired),
        isActive: Boolean(form.isActive)
    };
}

export default function QuestionsManager({
    questions,
    onChange,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
    generateQuestions = null,
    allowGenerate = false,
    allowIA = false,
    emptyMessage = "Aucune question pour le moment.",
    addLabel = "Ajouter une question"
}) {
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [generating, setGenerating] = useState(null);

    const list = questions ?? [];

    const openAdd = () => {
        setEditingId(null);
        setForm(emptyForm());
        setDialogVisible(true);
    };

    const openEdit = (question) => {
        setEditingId(question.publicId);
        setForm(toForm(question));
        setDialogVisible(true);
    };

    const submit = async () => {
        const input = toInput(form);
        if (!input.label) {
            toast.warn("Le libellé de la question est obligatoire.");
            return;
        }
        if (input.type === "Choice" && (input.options?.length ?? 0) < 2) {
            toast.warn("Une question à choix doit proposer au moins deux options (séparées par des virgules).");
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                const updated = await updateQuestion(editingId, input);
                onChange(list.map((question) => (question.publicId === editingId ? { ...question, ...updated } : question)));
                toast.success("Question mise à jour.");
            } else {
                const created = await addQuestion(input);
                onChange([...list, created]);
                toast.success("Question ajoutée.");
            }
            setDialogVisible(false);
        } catch (error) {
            toast.error(error?.message || "Enregistrement impossible.");
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = (event, question) => {
        confirmPopup({
            target: event.currentTarget,
            message: question.answersCount > 0
                ? "Des locataires ont déjà répondu : la question sera désactivée (les réponses sont conservées). Continuer ?"
                : "Supprimer cette question ?",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Oui",
            rejectLabel: "Non",
            accept: async () => {
                setBusyId(question.publicId);
                try {
                    const result = await deleteQuestion(question.publicId);
                    if (result?.deleted) {
                        onChange(list.filter((item) => item.publicId !== question.publicId));
                    } else {
                        onChange(list.map((item) => (item.publicId === question.publicId ? { ...item, isActive: false } : item)));
                    }
                    toast.info(result?.message || "Question supprimée.");
                } catch (error) {
                    toast.error(error?.message || "Suppression impossible.");
                } finally {
                    setBusyId(null);
                }
            }
        });
    };

    const move = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= list.length) {
            return;
        }
        const reordered = [...list];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        onChange(reordered);
        try {
            const saved = await reorderQuestions(reordered.map((question) => question.publicId));
            if (Array.isArray(saved)) {
                onChange(saved);
            }
        } catch (error) {
            toast.error(error?.message || "Réorganisation impossible.");
        }
    };

    const toggleActive = async (question) => {
        setBusyId(question.publicId);
        try {
            const updated = await updateQuestion(question.publicId, { ...toInput(toForm(question)), isActive: !question.isActive });
            onChange(list.map((item) => (item.publicId === question.publicId ? { ...item, ...updated } : item)));
        } catch (error) {
            toast.error(error?.message || "Mise à jour impossible.");
        } finally {
            setBusyId(null);
        }
    };

    const generate = (event, ia) => {
        confirmPopup({
            target: event.currentTarget,
            message: ia
                ? "Générer de nouvelles questions avec l'IA ? Les questions générées automatiquement seront remplacées (les questions saisies à la main sont conservées)."
                : "Régénérer les questions à partir du type de matériel ? Les questions générées automatiquement seront remplacées (les questions saisies à la main sont conservées).",
            icon: "pi pi-sparkles",
            acceptLabel: "Générer",
            rejectLabel: "Annuler",
            accept: async () => {
                setGenerating(ia ? "ia" : "rules");
                try {
                    const result = await generateQuestions(ia);
                    onChange(result?.questions ?? []);
                    toast.success(ia ? "Questions générées par l'IA." : "Questions régénérées.");
                } catch (error) {
                    toast.error(error?.message || "Génération impossible.");
                } finally {
                    setGenerating(null);
                }
            }
        });
    };

    return (
        <div>
            <ConfirmPopup />
            {list.length === 0 && <p className="sat-empty">{emptyMessage}</p>}
            <div className="sat-manager">
                {list.map((question, index) => (
                    <div key={question.publicId} className={`sat-manager-row${question.isActive ? "" : " is-inactive"}`}>
                        <div className="sat-manager-order">
                            <Button icon="pi pi-chevron-up" text size="small" aria-label="Monter" disabled={index === 0} onClick={() => move(index, -1)} />
                            <Button icon="pi pi-chevron-down" text size="small" aria-label="Descendre" disabled={index === list.length - 1} onClick={() => move(index, 1)} />
                        </div>
                        <div className="sat-manager-main">
                            <strong>{question.label}</strong>
                            <div className="sat-manager-tags">
                                <span className="sat-tag">{questionTypeLabel(question.type)}</span>
                                {question.type === "Choice" && (question.options ?? []).length > 0 && (
                                    <span className="sat-tag">{question.options.join(" · ")}</span>
                                )}
                                {question.isRequired && <span className="sat-tag is-required">Obligatoire</span>}
                                {!question.isActive && <span className="sat-tag is-inactive">Désactivée</span>}
                                {question.isGenerated && <span className="sat-tag is-generated">Générée</span>}
                                {question.answersCount > 0 && <span className="sat-tag">{question.answersCount} réponse{question.answersCount > 1 ? "s" : ""}</span>}
                            </div>
                        </div>
                        <div className="sat-manager-actions">
                            <Button
                                icon={question.isActive ? "pi pi-eye-slash" : "pi pi-eye"}
                                text
                                rounded
                                size="small"
                                aria-label={question.isActive ? "Désactiver" : "Réactiver"}
                                tooltip={question.isActive ? "Ne plus poser cette question" : "Poser à nouveau cette question"}
                                loading={busyId === question.publicId}
                                onClick={() => toggleActive(question)}
                            />
                            <Button icon="pi pi-pencil" text rounded size="small" aria-label="Modifier" onClick={() => openEdit(question)} />
                            <Button icon="pi pi-trash" text rounded size="small" severity="danger" aria-label="Supprimer" loading={busyId === question.publicId} onClick={(event) => confirmDelete(event, question)} />
                        </div>
                    </div>
                ))}
            </div>

            <div className="sat-manager-toolbar">
                <Button label={addLabel} icon="pi pi-plus" size="small" onClick={openAdd} />
                {allowGenerate && generateQuestions && (
                    <Button label="Régénérer (type de matériel)" icon="pi pi-refresh" size="small" severity="secondary" outlined loading={generating === "rules"} onClick={(event) => generate(event, false)} />
                )}
                {allowIA && generateQuestions && (
                    <Button label="Générer avec l'IA" icon="pi pi-sparkles" size="small" severity="secondary" outlined loading={generating === "ia"} onClick={(event) => generate(event, true)} />
                )}
            </div>

            <Dialog
                header={editingId ? "Modifier la question" : "Nouvelle question"}
                visible={dialogVisible}
                style={{ width: "min(560px, 95vw)" }}
                onHide={() => setDialogVisible(false)}
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <Button label="Annuler" text onClick={() => setDialogVisible(false)} />
                        <Button label="Enregistrer" icon="pi pi-check" loading={saving} onClick={submit} />
                    </div>
                )}
            >
                <div className="sat-dialog-field">
                    <label htmlFor="sat-q-label">Question</label>
                    <InputText id="sat-q-label" value={form.label} maxLength={300} autoFocus onChange={(event) => setForm({ ...form, label: event.target.value })} placeholder="Ex. : La puissance était-elle suffisante pour votre usage ?" />
                    <small>Courte et concrète : le locataire doit pouvoir répondre en quelques secondes.</small>
                </div>
                <div className="sat-dialog-field">
                    <label htmlFor="sat-q-type">Type de réponse</label>
                    <Dropdown id="sat-q-type" value={form.type} options={QUESTION_TYPES} onChange={(event) => setForm({ ...form, type: event.value })} />
                </div>
                {form.type === "Choice" && (
                    <div className="sat-dialog-field">
                        <label htmlFor="sat-q-options">Options</label>
                        <InputText id="sat-q-options" value={form.options} onChange={(event) => setForm({ ...form, options: event.target.value })} placeholder="Terrasse, Véhicule, Façade, Autre" />
                        <small>Séparez les options par des virgules (2 à 6 options).</small>
                    </div>
                )}
                <div className="sat-dialog-check">
                    <Checkbox inputId="sat-q-required" checked={form.isRequired} onChange={(event) => setForm({ ...form, isRequired: event.checked })} />
                    <label htmlFor="sat-q-required">Réponse obligatoire (à utiliser avec parcimonie)</label>
                </div>
                {editingId && (
                    <div className="sat-dialog-check">
                        <Checkbox inputId="sat-q-active" checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.checked })} />
                        <label htmlFor="sat-q-active">Question active (posée aux prochains locataires)</label>
                    </div>
                )}
            </Dialog>
        </div>
    );
}
